export const SITE_ORIGIN = 'https://vadauro.com';
const description = 'Explore VADAURO clothing, custom sizing, and virtual try-on.';
const titleKeys: Record<string, string> = {
  '/': 'metadata.title.home',
  '/shop': 'metadata.title.shop',
  '/shop/explore': 'metadata.title.shopExplore',
  '/salon-studio': 'metadata.title.salonStudio',
  '/deep-customization': 'metadata.title.customClothing',
  '/clothing-custom': 'metadata.title.customClothing',
  '/hairstyle-design': 'metadata.title.hairstyleDesign',
  '/measurement': 'metadata.title.measurement',
  '/measurement/scan': 'metadata.title.measurementScan',
  '/login': 'metadata.title.login',
  '/reset-password': 'metadata.title.resetPassword',
  '/create-account': 'metadata.title.createAccount',
  '/verify-email': 'metadata.title.verifyEmail',
  '/generate-avatar': 'metadata.title.generateAvatar',
  '/style-preferences': 'metadata.title.stylePreferences',
  '/try-on/upload': 'metadata.title.tryOn',
  '/my-ai-photos': 'metadata.title.aiPhotos',
  '/settings': 'metadata.title.settings',
  '/shopping-cart': 'metadata.title.shoppingCart',
  '/order-summary': 'metadata.title.orderSummary',
  '/checkout': 'metadata.title.checkout',
  '/orders': 'metadata.title.orders',
  '/admin/orders': 'metadata.title.adminOrders',
  '/nail-custom': 'metadata.title.nailCustomization',
  '/about': 'metadata.title.about',
  '/our-story': 'metadata.title.ourStory',
  '/fabrics': 'metadata.title.fabrics',
  '/customization': 'metadata.title.customization',
  '/contact': 'metadata.title.contact',
  '/business-partnership': 'metadata.title.businessPartnerships',
  '/factory-upload': 'metadata.title.factoryUpload',
  '/custom-order-return-policy': 'metadata.title.returnPolicy',
  '/privacy': 'metadata.title.privacy',
  '/cookie-policy': 'metadata.title.cookiePolicy',
  '/terms': 'metadata.title.terms',
  '/unknown': 'metadata.title.notFound',
};
const pages: Record<string, [string, string]> = {
  '/': ['VADAURO | Clothing & Virtual Try-On', description],
  '/shop': ['Shop Clothing | VADAURO', 'Browse the VADAURO clothing collection and find your fit.'],
  '/shop/explore': ['Explore the Collection | VADAURO', 'Discover VADAURO fabrics, styles, and custom clothing.'],
  '/salon-studio': ['Salon Collection | VADAURO', 'Explore the VADAURO salon collection.'],
  '/deep-customization': ['Custom Clothing | VADAURO', 'Explore custom clothing options at VADAURO.'],
  '/clothing-custom': ['Custom Clothing | VADAURO', 'Explore custom clothing options at VADAURO.'],
  '/about': ['About VADAURO', 'Learn about VADAURO clothing and virtual try-on.'],
  '/our-story': ['Our Story | VADAURO', 'The story behind VADAURO.'],
  '/contact': ['Contact | VADAURO', 'Contact the VADAURO team for help with your account or order.'],
  '/business-partnership': ['Business Partnerships | VADAURO', 'Explore business partnerships with VADAURO.'],
  '/custom-order-return-policy': ['Custom Order Return Policy | VADAURO', 'Read the VADAURO custom order return policy.'],
  '/privacy': ['Privacy Policy | VADAURO', 'Read how VADAURO handles personal information.'],
  '/cookie-policy': ['Cookie Policy | VADAURO', 'Read about cookies and your privacy choices on VADAURO.'],
  '/terms': ['Terms of Service | VADAURO', 'Read the VADAURO terms of service.'],
  '/try-on/upload': ['Virtual Clothing Try-On | VADAURO', 'Preview clothing with VADAURO virtual try-on.'],
  '/measurement': ['Body Measurements | VADAURO', 'Create your VADAURO measurement profile.'],
  '/measurement/scan': ['AI Body Scan Survey | VADAURO', 'Complete your fit survey and create your VADAURO body measurement profile.'],
};

export function getPageMetadata(
  path: string,
  product?: { name: string; description?: string },
  search = '',
  translateTitle?: (key: string, fallback: string) => string,
) {
  const pathname = path.replace(/\/+$/, '') || '/';
  const page = pages[pathname];
  const productPage = pathname.startsWith('/product/') && Boolean(product?.name);
  // AR share links are private, per-model pages; they keep noindex like other unlisted paths.
  const arShare = pathname.startsWith('/ar/');
  const fallbackTitle = page?.[0] ?? (arShare ? 'View in AR | VADAURO' : undefined) ?? ({
      '/checkout': 'Checkout | VADAURO', '/settings': 'Account Settings | VADAURO',
      '/login': 'Sign In | VADAURO', '/create-account': 'Create an Account | VADAURO',
      '/reset-password': 'Reset Password | VADAURO', '/verify-email': 'Verify Email | VADAURO',
      '/shopping-cart': 'Shopping Cart | VADAURO', '/orders': 'Order History | VADAURO',
      '/factory-upload': 'Factory Upload | VADAURO', '/admin/orders': 'Order Administration | VADAURO',
      '/nail-custom': 'Nail Customization — Coming Soon | VADAURO',
      '/hairstyle-design': 'Hairstyle Design — Coming Soon | VADAURO',
    } as Record<string, string>)[pathname] ?? 'Page Not Found | VADAURO';
  const titleKey = titleKeys[pathname] ?? (arShare ? 'metadata.title.arView' : titleKeys['/unknown']);
  return {
    title: productPage
      ? `${product!.name.slice(0, 140)} | VADAURO`
      : translateTitle?.(titleKey, fallbackTitle) ?? fallbackTitle,
    description: (productPage ? product?.description || description : page?.[1] ?? description).slice(0, 300),
    canonical: `${SITE_ORIGIN}${pathname === '/clothing-custom' ? '/deep-customization' : pathname === '/' ? '/' : pathname}`,
    robots: (!page && !productPage) || new URLSearchParams(search).get('kind') === 'exclusive'
      ? 'noindex, nofollow' : 'index, follow',
  };
}

export function updatePageMetadata(metadata: ReturnType<typeof getPageMetadata>) {
  document.title = metadata.title;
  const set = (attribute: string, key: string, content: string) => {
    let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attribute, key);
      document.head.append(element);
    }
    element.content = content;
  };
  set('name', 'description', metadata.description);
  set('name', 'robots', metadata.robots);
  for (const prefix of ['og', 'twitter']) {
    const attribute = prefix === 'og' ? 'property' : 'name';
    set(attribute, `${prefix}:title`, metadata.title);
    set(attribute, `${prefix}:description`, metadata.description);
  }
  set('property', 'og:url', metadata.canonical);
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.append(canonical);
  }
  canonical.href = metadata.canonical;
}

export function renderMetadataHead(metadata: ReturnType<typeof getPageMetadata>) {
  const escape = (value: string) => value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!);
  const title = escape(metadata.title);
  const summary = escape(metadata.description);
  const canonical = escape(metadata.canonical);
  return `<title>${title}</title><meta name="description" content="${summary}"><meta name="robots" content="${metadata.robots}"><link rel="canonical" href="${canonical}"><meta property="og:title" content="${title}"><meta property="og:description" content="${summary}"><meta property="og:url" content="${canonical}"><meta name="twitter:title" content="${title}"><meta name="twitter:description" content="${summary}">`;
}
